import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Text} from "@pg-design/text-module";

import {trackPricesHubPlaceClick} from "../../tracking/track_prices_hub_place_click";
import {formatPriceByUnit, formatSignedPercent} from "../../utils/format_price_m2";
import {formatRcnPeriodLabel} from "../../utils/format_rcn_period_label";
import {getCityLink} from "../../utils/get_city_link";
import {ITransactionPriceRow} from "../../utils/get_transaction_price_rows";
import {PricesHubCityIcon} from "./PricesHubCityIcon";
import {PricesHubTrendChip} from "./PricesHubTrendChip";

interface IProps {
    rows: ITransactionPriceRow[];
    dominantPeriodLabel: string;
    offerMonthLabel: string | null;
}

export const PricesHubTransactionPricesCards = (props: IProps) => {
    const {rows, dominantPeriodLabel, offerMonthLabel} = props;
    const dominantPeriod = formatRcnPeriodLabel(dominantPeriodLabel);

    return (
        <ul className={listStyle}>
            {rows.map((row) => (
                <li key={row.slug} className={itemStyle}>
                    <article className={cardStyle} aria-label={`${row.name} – ceny mieszkań`}>
                        <header className={cardHeaderStyle}>
                            <span className={cityStyle}>
                                <PricesHubCityIcon slug={row.slug} size="2.8" />
                                <Text as="span" variant="body_copy_1" preserveFontSize className={cityNameStyle}>
                                    {row.name}
                                </Text>
                            </span>

                            <PricesHubTrendChip size="large" isBelow={row.differencePct <= 0} hiddenLabel="różnica:">
                                {formatSignedPercent(row.differencePct)}
                            </PricesHubTrendChip>
                        </header>

                        <dl className={pricesStyle}>
                            <div className={priceRowStyle}>
                                <Text as="dt" variant="info_txt_1" className={priceLabelStyle}>
                                    Aktualna cena ofertowa{offerMonthLabel ? ` (${offerMonthLabel})` : null}
                                </Text>
                                <Text as="dd" variant="body_copy_1" className={priceValueStyle}>
                                    {formatPriceByUnit(row.offerPriceM2, row.unit)}
                                </Text>
                            </div>

                            <hr className={separatorStyle} />

                            <div className={priceRowStyle}>
                                <Text as="dt" variant="info_txt_1" className={priceLabelStyle}>
                                    Cena ofertowa ({dominantPeriod})
                                </Text>
                                <Text as="dd" variant="body_copy_1" className={priceValueStyle}>
                                    {row.periodOfferPriceM2 === null ? "—" : formatPriceByUnit(row.periodOfferPriceM2, row.unit)}
                                    {row.periodLabel === dominantPeriodLabel ? null : (
                                        <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                                            {formatRcnPeriodLabel(row.periodLabel)}
                                        </Text>
                                    )}
                                </Text>
                            </div>
                            <div className={priceRowStyle}>
                                <Text as="dt" variant="info_txt_1" className={priceLabelStyle}>
                                    Cena transakcyjna ({dominantPeriod})
                                </Text>
                                <Text as="dd" variant="body_copy_1" className={priceValueStyle}>
                                    {formatPriceByUnit(row.transactionPriceM2, row.unit)}
                                    {row.periodLabel === dominantPeriodLabel ? null : (
                                        <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                                            {formatRcnPeriodLabel(row.periodLabel)}
                                        </Text>
                                    )}
                                </Text>
                            </div>
                        </dl>

                        <Button
                            variant="outlined_secondary"
                            size="x-small"
                            fullWidth
                            href={getCityLink(row.slug)}
                            className={ctaStyle}
                            onClick={() => trackPricesHubPlaceClick(row)}
                        >
                            Zobacz ceny
                        </Button>
                    </article>
                </li>
            ))}
        </ul>
    );
};

const listStyle = css({
    display: "flex",
    gap: 1.5,
    width: "100%",
    margin: 0,
    py: 0.5,
    px: 0,
    listStyle: "none",
    overflowX: "auto",
    scrollbarWidth: "none",
    scrollSnapType: "x mandatory",

    "&::-webkit-scrollbar": {
        display: "none"
    }
});

const itemStyle = css({
    flex: "0 0 auto",
    width: "min(26.4rem, 100%)",
    scrollSnapAlign: "start"
});

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: "1.3rem",
    boxSizing: "border-box",
    height: "100%",
    padding: 2,
    backgroundColor: "var(--colors-white)",
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2,
    boxShadow: "0 6px 16px 0 rgba(36, 36, 46, 0.1)"
});

const cardHeaderStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1
});

const cityStyle = css({
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    minWidth: 0
});

const cityNameStyle = css({
    overflow: "hidden",
    color: "var(--colors-secondary)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const pricesStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    margin: 0
});

/** Splits the portal's own current price from the block that belongs to the transaction period. */
const separatorStyle = css({
    width: "100%",
    height: 0,
    margin: 0,
    border: 0,
    borderTop: "1px solid var(--colors-gray-300)"
});

const priceRowStyle = css({
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 1
});

const priceLabelStyle = css({
    color: "var(--colors-gray-800)"
});

const priceValueStyle = css({
    margin: 0,
    color: "var(--colors-secondary)",
    textAlign: "right",
    whiteSpace: "nowrap"
});

const periodNoteStyle = css({
    display: "block",
    color: "var(--colors-gray-700)"
});

const ctaStyle = css({
    marginTop: "auto"
});
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Text} from "@pg-design/text-module";

import {trackPricesHubPlaceClick} from "../../tracking/track_prices_hub_place_click";
import {formatPriceByUnit, formatSignedPercent} from "../../utils/format_price_m2";
import {formatRcnPeriodLabel} from "../../utils/format_rcn_period_label";
import {getCityLink} from "../../utils/get_city_link";
import {ITransactionPriceRow} from "../../utils/get_transaction_price_rows";
import {PricesHubCityIcon} from "./PricesHubCityIcon";
import {PricesHubTrendChip} from "./PricesHubTrendChip";

interface IProps {
    rows: ITransactionPriceRow[];
    dominantPeriodLabel: string;
    offerMonthLabel: string | null;
}

export const PricesHubTransactionPricesCards = (props: IProps) => {
    const {rows, dominantPeriodLabel, offerMonthLabel} = props;
    const dominantPeriod = formatRcnPeriodLabel(dominantPeriodLabel);

    return (
        <ul className={listStyle}>
            {rows.map((row) => (
                <li key={row.slug} className={itemStyle}>
                    <article className={cardStyle} aria-label={`${row.name} – ceny mieszkań`}>
                        <header className={cardHeaderStyle}>
                            <span className={cityStyle}>
                                <PricesHubCityIcon slug={row.slug} size="2.8" />
                                <Text as="span" variant="body_copy_1" preserveFontSize className={cityNameStyle}>
                                    {row.name}
                                </Text>
                            </span>

                            <PricesHubTrendChip size="large" isBelow={row.differencePct <= 0} hiddenLabel="różnica:">
                                {formatSignedPercent(row.differencePct)}
                            </PricesHubTrendChip>
                        </header>

                        <dl className={pricesStyle}>
                            <div className={priceRowStyle}>
                                <Text as="dt" variant="info_txt_1" className={priceLabelStyle}>
                                    Aktualna cena ofertowa{offerMonthLabel ? ` (${offerMonthLabel})` : null}
                                </Text>
                                <Text as="dd" variant="body_copy_1" className={priceValueStyle}>
                                    {formatPriceByUnit(row.offerPriceM2, row.unit)}
                                </Text>
                            </div>

                            <hr className={separatorStyle} />

                            <div className={priceRowStyle}>
                                <Text as="dt" variant="info_txt_1" className={priceLabelStyle}>
                                    Cena ofertowa ({dominantPeriod})
                                </Text>
                                <Text as="dd" variant="body_copy_1" className={priceValueStyle}>
                                    {row.periodOfferPriceM2 === null ? "—" : formatPriceByUnit(row.periodOfferPriceM2, row.unit)}
                                    {row.periodLabel === dominantPeriodLabel ? null : (
                                        <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                                            {formatRcnPeriodLabel(row.periodLabel)}
                                        </Text>
                                    )}
                                </Text>
                            </div>
                            <div className={priceRowStyle}>
                                <Text as="dt" variant="info_txt_1" className={priceLabelStyle}>
                                    Cena transakcyjna ({dominantPeriod})
                                </Text>
                                <Text as="dd" variant="body_copy_1" className={priceValueStyle}>
                                    {formatPriceByUnit(row.transactionPriceM2, row.unit)}
                                    {row.periodLabel === dominantPeriodLabel ? null : (
                                        <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                                            {formatRcnPeriodLabel(row.periodLabel)}
                                        </Text>
                                    )}
                                </Text>
                            </div>
                        </dl>

                        <Button
                            variant="outlined_secondary"
                            size="x-small"
                            fullWidth
                            href={getCityLink(row.slug)}
                            className={ctaStyle}
                            onClick={() => trackPricesHubPlaceClick(row)}
                        >
                            Zobacz ceny
                        </Button>
                    </article>
                </li>
            ))}
        </ul>
    );
};

const listStyle = css({
    display: "flex",
    gap: 1.5,
    width: "100%",
    margin: 0,
    py: 0.5,
    px: 0,
    listStyle: "none",
    overflowX: "auto",
    scrollbarWidth: "none",
    scrollSnapType: "x mandatory",

    "&::-webkit-scrollbar": {
        display: "none"
    }
});

const itemStyle = css({
    flex: "0 0 auto",
    width: "min(26.4rem, 100%)",
    scrollSnapAlign: "start"
});

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: "1.3rem",
    boxSizing: "border-box",
    height: "100%",
    padding: 2,
    backgroundColor: "var(--colors-white)",
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2,
    boxShadow: "0 6px 16px 0 rgba(36, 36, 46, 0.1)"
});

const cardHeaderStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1
});

const cityStyle = css({
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    minWidth: 0
});

const cityNameStyle = css({
    overflow: "hidden",
    color: "var(--colors-secondary)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const pricesStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    margin: 0
});

/** Splits the portal's own current price from the block that belongs to the transaction period. */
const separatorStyle = css({
    width: "100%",
    height: 0,
    margin: 0,
    border: 0,
    borderTop: "1px solid var(--colors-gray-300)"
});

const priceRowStyle = css({
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 1
});

const priceLabelStyle = css({
    color: "var(--colors-gray-800)"
});

const priceValueStyle = css({
    margin: 0,
    color: "var(--colors-secondary)",
    textAlign: "right",
    whiteSpace: "nowrap"
});

const periodNoteStyle = css({
    display: "block",
    color: "var(--colors-gray-700)"
});

const ctaStyle = css({
    marginTop: "auto"
});
